import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { BehaviorSubject, combineLatest, Observable, of, startWith, SubscriptionLike, tap } from 'rxjs';
import { Big } from 'big.js';
import { TransferDirection } from '../../../models';
import { fractionLength, itemUnsubscribe, NEON, priorityFeeLamports, SOL, TRANSACTION_FEE } from '../../../utils';
import { PriorityFeeService, PythService, TokenTransferFeeService, TokenTransferService } from '../../services';

@Component({
  selector: 'app-transfer-gas-fees',
  templateUrl: './transfer-gas-fees.component.html',
  styleUrl: './transfer-gas-fees.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransferGasFeesComponent implements OnInit, OnDestroy {
  @Input() direction: TransferDirection;
  @Input() reward: 'solana' | 'neon' = 'solana';
  @Input() loading: Observable<boolean> = of(false);
  private subs: SubscriptionLike[] = [];

  // Fee in tokens (shown as the value) and in USD (shown in the tooltip).
  transferFee$ = new BehaviorSubject('');
  transferFeeUSD$ = new BehaviorSubject('');

  ngOnInit() {
    this.subs.push(combineLatest([
      this.fee.transferFeeView$,
      this.priorityFee.selected$,
      this.priorityFee.data$,
      this.pyth.data$.pipe(startWith(null))
    ]).pipe(
      tap(([{ solanaFee, neonFee }, priorityFee, data]) => {
        let sFee = '';
        let nFee = '';
        let feeUSD = new Big(0);
        if (this.direction === TransferDirection.solana) {
          if (priorityFee?.amount > 0) {
            const pFee = priorityFeeLamports(priorityFee.amount, data.priorityFee.units);
            const sFeeBig = pFee.add(TRANSACTION_FEE).div(LAMPORTS_PER_SOL);
            feeUSD = sFeeBig.times(this.pyth.solPrice);
            if (this.reward === 'neon') {
              // SOL fee paid in NEON: SOL amount * (NEON per SOL)
              const fee = sFeeBig.times(this.pyth.solPerNeon);
              if (fee.gt(0)) {
                nFee = `${fee.toFixed(fractionLength(fee.toNumber()))} ${NEON}`;
              }
            } else {
              sFee = `${sFeeBig.toFixed(fractionLength(sFeeBig.toNumber()))} ${SOL}`;
            }
          }
        } else {
          if (solanaFee.gt(0)) {
            const sFeeBig = new Big(solanaFee);
            sFee = `${sFeeBig.toFixed(fractionLength(sFeeBig.toNumber()))} ${SOL}`;
            feeUSD = feeUSD.add(sFeeBig.times(this.pyth.solPrice));
          }
          if (neonFee?.gt(0)) {
            const nFeeBig = new Big(neonFee);
            nFee = `${nFeeBig.toFixed(fractionLength(nFeeBig.toNumber()))} ${NEON}`;
            feeUSD = feeUSD.add(nFeeBig.times(this.pyth.neonPrice));
          }
        }
        this.transferFee$.next([sFee, nFee].filter(Boolean).join(' + '));
        this.transferFeeUSD$.next(this.usdView(feeUSD));
      })).subscribe());
  }

  // Empty when prices are unknown: the tooltip is hidden rather than showing a wrong value.
  usdView(feeUSD: Big): string {
    if (feeUSD.lte(0)) {
      return '';
    }
    return feeUSD.lt(1e-4) ? '<$0.0001' : `$${parseFloat(feeUSD.toFixed(4))}`;
  }

  ngOnDestroy() {
    itemUnsubscribe(this.subs);
  }

  constructor(public transfer: TokenTransferService, private pyth: PythService, private fee: TokenTransferFeeService,
              private priorityFee: PriorityFeeService) {
  }
}
